import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Notifications,
  Security,
  Language,
  Palette,
  Business,
  Payment,
  Help,
  Logout,
  Delete,
  Add,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';

const mockUserData = {
  id: 1,
  name: 'John Smith',
  email: 'john.smith@glamourbeauty.com',
  phone: '+1 (555) 123-4567',
  role: 'Owner',
  joinDate: '2023-01-15',
};

const mockNotificationSettings = {
  emailNotifications: true,
  pushNotifications: true,
  smsNotifications: false,
  bookingConfirmations: true,
  cancellationAlerts: true,
  paymentNotifications: true,
  marketingEmails: false,
  weeklyReports: true,
};

const mockBusinessSettings = {
  businessName: 'Glamour Beauty Salon',
  timezone: 'America/New_York',
  currency: 'USD',
  language: 'en',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
  bookingWindow: 30,
  cancellationPolicy: 24,
  autoConfirmBookings: false,
};

const Settings = () => {
  const { user, logout } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  const [profileData, setProfileData] = useState(mockUserData);
  const [notificationSettings, setNotificationSettings] = useState(mockNotificationSettings);
  const [businessSettings, setBusinessSettings] = useState(mockBusinessSettings);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handleNotificationChange = (field, value) => {
    setNotificationSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleBusinessChange = (field, value) => {
    setBusinessSettings(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      // API call to update profile
      showSuccess('Profile updated successfully!');
      setEditMode(false);
    } catch (error) {
      showError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifications = async () => {
    try {
      setLoading(true);
      // API call to update notification settings
      showSuccess('Notification settings updated successfully!');
    } catch (error) {
      showError('Failed to update notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBusiness = async () => {
    try {
      setLoading(true);
      // API call to update business settings
      showSuccess('Business settings updated successfully!');
    } catch (error) {
      showError('Failed to update business settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError('New passwords do not match');
      return;
    }
    
    try {
      setLoading(true);
      // API call to change password
      showSuccess('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      showError('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      // API call to delete account
      showSuccess('Account deleted successfully');
      logout();
    } catch (error) {
      showError('Failed to delete account');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const TabPanel = ({ children, value, index }) => {
    return (
      <div hidden={value !== index}>
        {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
      </div>
    );
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        Settings
      </Typography>

      <Card>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Business />} label="Profile" />
          <Tab icon={<Notifications />} label="Notifications" />
          <Tab icon={<Business />} label="Business" />
          <Tab icon={<Security />} label="Security" />
          <Tab icon={<Help />} label="Support" />
        </Tabs>

        <CardContent>
          {/* Profile Tab */}
          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Avatar
                      sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: 36, fontWeight: 600 }}
                    >
                      {profileData.name?.charAt(0)?.toUpperCase() || 'A'}
                    </Avatar>
                    <Typography variant="h6">{profileData.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {profileData.role}
                    </Typography>
                    <Chip
                      label={`Member since ${new Date(profileData.joinDate).getFullYear()}`}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">Personal Information</Typography>
                      {editMode ? (
                        <Box>
                          <Button
                            variant="outlined"
                            startIcon={<Cancel />}
                            onClick={() => setEditMode(false)}
                            sx={{ mr: 1 }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<Save />}
                            onClick={handleSaveProfile}
                            disabled={loading}
                          >
                            Save
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          variant="outlined"
                          startIcon={<Edit />}
                          onClick={() => setEditMode(true)}
                        >
                          Edit
                        </Button>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={profileData.name}
                      onChange={(e) => handleProfileChange('name', e.target.value)}
                      disabled={!editMode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={profileData.email}
                      onChange={(e) => handleProfileChange('email', e.target.value)}
                      disabled={!editMode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={profileData.phone}
                      onChange={(e) => handleProfileChange('phone', e.target.value)}
                      disabled={!editMode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Role"
                      value={profileData.role}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Notifications Tab */}
          <TabPanel value={activeTab} index={1}>
            <Typography variant="h6" gutterBottom>
              Notification Preferences
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  General Notifications
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText primary="Email Notifications" secondary="Receive notifications via email" />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.emailNotifications}
                        onChange={(e) => handleNotificationChange('emailNotifications', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Push Notifications" secondary="Receive push notifications" />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.pushNotifications}
                        onChange={(e) => handleNotificationChange('pushNotifications', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="SMS Notifications" secondary="Receive notifications via SMS" />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.smsNotifications}
                        onChange={(e) => handleNotificationChange('smsNotifications', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Business Notifications
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText primary="Booking Confirmations" secondary="New booking confirmations" />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.bookingConfirmations}
                        onChange={(e) => handleNotificationChange('bookingConfirmations', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Cancellation Alerts" secondary="Booking cancellation alerts" />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.cancellationAlerts}
                        onChange={(e) => handleNotificationChange('cancellationAlerts', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Payment Notifications" secondary="Payment and transaction alerts" />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.paymentNotifications}
                        onChange={(e) => handleNotificationChange('paymentNotifications', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Weekly Reports" secondary="Weekly business reports" />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.weeklyReports}
                        onChange={(e) => handleNotificationChange('weeklyReports', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </Grid>
            </Grid>
            <Box mt={3}>
              <Button
                variant="contained"
                onClick={handleSaveNotifications}
                disabled={loading}
              >
                Save Notification Settings
              </Button>
            </Box>
          </TabPanel>

          {/* Business Tab */}
          <TabPanel value={activeTab} index={2}>
            <Typography variant="h6" gutterBottom>
              Business Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={businessSettings.businessName}
                  onChange={(e) => handleBusinessChange('businessName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={businessSettings.timezone}
                    label="Timezone"
                    onChange={(e) => handleBusinessChange('timezone', e.target.value)}
                  >
                    <MenuItem value="America/New_York">Eastern Time</MenuItem>
                    <MenuItem value="America/Chicago">Central Time</MenuItem>
                    <MenuItem value="America/Denver">Mountain Time</MenuItem>
                    <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={businessSettings.currency}
                    label="Currency"
                    onChange={(e) => handleBusinessChange('currency', e.target.value)}
                  >
                    <MenuItem value="USD">USD - US Dollar</MenuItem>
                    <MenuItem value="EUR">EUR - Euro</MenuItem>
                    <MenuItem value="GBP">GBP - British Pound</MenuItem>
                    <MenuItem value="CAD">CAD - Canadian Dollar</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={businessSettings.language}
                    label="Language"
                    onChange={(e) => handleBusinessChange('language', e.target.value)}
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="es">Spanish</MenuItem>
                    <MenuItem value="fr">French</MenuItem>
                    <MenuItem value="de">German</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Booking Window (days)"
                  type="number"
                  value={businessSettings.bookingWindow}
                  onChange={(e) => handleBusinessChange('bookingWindow', parseInt(e.target.value))}
                  helperText="How far in advance customers can book"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cancellation Policy (hours)"
                  type="number"
                  value={businessSettings.cancellationPolicy}
                  onChange={(e) => handleBusinessChange('cancellationPolicy', parseInt(e.target.value))}
                  helperText="Minimum notice required for cancellation"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={businessSettings.autoConfirmBookings}
                      onChange={(e) => handleBusinessChange('autoConfirmBookings', e.target.checked)}
                    />
                  }
                  label="Auto-confirm bookings"
                />
              </Grid>
            </Grid>
            <Box mt={3}>
              <Button
                variant="contained"
                onClick={handleSaveBusiness}
                disabled={loading}
              >
                Save Business Settings
              </Button>
            </Box>
          </TabPanel>

          {/* Security Tab */}
          <TabPanel value={activeTab} index={3}>
            <Typography variant="h6" gutterBottom>
              Security Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Change Password
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleChangePassword}
                      disabled={loading || !passwordData.currentPassword || !passwordData.newPassword}
                    >
                      Change Password
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom color="error">
                  Danger Zone
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Deleting your account is permanent and cannot be undone.
                </Alert>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete Account
                </Button>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Support Tab */}
          <TabPanel value={activeTab} index={4}>
            <Typography variant="h6" gutterBottom>
              Support & Help
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Contact Support
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Need help? Our support team is here to assist you.
                </Typography>
                <Button variant="contained" sx={{ mb: 2 }}>
                  Contact Support
                </Button>
                <Typography variant="subtitle1" gutterBottom>
                  Documentation
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Learn how to make the most of your salon management system.
                </Typography>
                <Button variant="outlined">
                  View Documentation
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  System Information
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText primary="Version" secondary="1.0.0" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Last Updated" secondary="January 2024" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Support Email" secondary="support@glamapp.com" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </TabPanel>
        </CardContent>
      </Card>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone. All your data will be permanently deleted.
          </Alert>
          <Typography>
            Are you sure you want to delete your account? This will permanently remove all your salon data, bookings, and settings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;